// Canonical domain enums for Centro Cirúrgico Control.
//
// This package is the single source of truth for the values shared between the API
// worker and the apps. Keep these in sync with the migrations:
//   - migrations/core/0002_identity_rbac.sql + 0006  -> roles
//   - migrations/core/0001_foundation.sql            -> subscription plans / room states
//   - migrations/core/0010_patient_room_flow.sql     -> patient flow enums (item 5)
//   - migrations/core/0012_professional_profile.sql  -> professional profile enums (item 4)
//   - migrations/facial/0005_access_event_blocked.sql -> recognition statuses
//   - feature_entitlements.capability                -> capabilities (see domain/entitlements)

export const roles = [
  "admin",
  "admin_global",
  "gerente",
  "somente_leitura",
  "coordinator",
  "reception",
  "room_operator",
  "supervisor",
  "auditor"
] as const;
export type Role = (typeof roles)[number];

/** Rótulos dos perfis em pt-BR (sidebar do admin, "Minha conta"). */
export const roleLabels: Readonly<Record<Role, string>> = {
  // Hierarquia do painel: Master (tudo) > Administrador (tudo, exceto usuários e modelos de mensagem).
  admin: "Master",
  admin_global: "Administrador",
  gerente: "Gerente",
  somente_leitura: "Somente leitura",
  coordinator: "Coordenação",
  reception: "Recepção",
  room_operator: "Operação de sala",
  supervisor: "Supervisão",
  auditor: "Auditoria"
};

/** Perfis que podem ser atribuídos a usuários do painel administrativo (tela Usuários). */
export const adminUserRoles = ["admin", "admin_global", "gerente", "somente_leitura"] as const;
export type AdminUserRole = (typeof adminUserRoles)[number];

export const adminUserStatuses = ["active", "suspended"] as const;
export type AdminUserStatus = (typeof adminUserStatuses)[number];

export const permissions = [
  "admin:self",
  "patient:read",
  "patient:write",
  "reception:write",
  "room:scan",
  "room:operate",
  "team:manage",
  "presence:override",
  "facial:review",
  "notifications:manage",
  "notifications:read",
  "reports:read",
  "audit:read",
  "subscription:manage",
  "org:read",
  "org:write",
  "org:delete",
  "users:manage",
  "templates:manage",
  "links:reveal"
] as const;
export type Permission = (typeof permissions)[number];

export const capabilities = [
  "patient-room-control",
  "clinical-team-control",
  "facial-validation",
  "team-compliance-reports"
] as const;
export type Capability = (typeof capabilities)[number];

// Subscription "plans" (basic | complete) — canonical plans enum.
export const subscriptionPlans = ["basic", "complete"] as const;
export type SubscriptionPlan = (typeof subscriptionPlans)[number];
export { subscriptionPlans as plans };
export type Plan = SubscriptionPlan;

export const subscriptionStatuses = ["active", "suspended", "cancelled"] as const;
export type SubscriptionStatus = (typeof subscriptionStatuses)[number];

/**
 * Estados da sala (item 5.3). O ciclo operacional é
 * free → occupied → cleaning (setup) → free; `blocked` é manutenção (só admin);
 * `preparation` e `finalization` ficam reservados para o ciclo completo do legado.
 */
export const roomStates = [
  "free",
  "preparation",
  "occupied",
  "finalization",
  "cleaning",
  "blocked"
] as const;
export type RoomState = (typeof roomStates)[number];

export const roomStateLabels: Readonly<Record<RoomState, string>> = {
  free: "Livre",
  preparation: "Em preparação",
  occupied: "Ocupada",
  finalization: "Em finalização",
  cleaning: "Em setup",
  blocked: "Bloqueada"
};

export const authMethods = ["password", "dev"] as const;
export type AuthMethod = (typeof authMethods)[number];

export const environments = ["development", "production"] as const;
export type Environment = (typeof environments)[number];

/** Ambiente de publicação (item 6): `prod` ou `dev-`. */
export const deployEnvironments = ["prod", "dev"] as const;
export type DeployEnvironment = (typeof deployEnvironments)[number];

/** Resultado de uma leitura facial no totem (item 4.5: `blocked` para inativo/fora da validade). */
export const recognitionStatuses = ["recognized", "unrecognized", "blocked"] as const;
export type RecognitionStatus = (typeof recognitionStatuses)[number];

/** Movimento registrado por uma leitura facial reconhecida: entrada ou saída do centro cirúrgico. */
export const accessDirections = ["entry", "exit"] as const;
export type AccessDirection = (typeof accessDirections)[number];
export const accessDirectionLabels: Record<AccessDirection, string> = {
  entry: "Entrada",
  exit: "Saída"
};

// ---------------------------------------------------------------------------
// Corpo clínico (item 4)
// ---------------------------------------------------------------------------

export const councilTypes = ["CRM", "CRO", "COREN", "CREFITO", "CRF", "CRBM", "CRTR", "OUTRO"] as const;
export type CouncilType = (typeof councilTypes)[number];

export const professionalCategories = [
  "medico_cirurgiao",
  "medico_anestesiologista",
  "medico_auxiliar",
  "medico_residente",
  "cirurgiao_dentista",
  "enfermeiro",
  "tecnico_enfermagem",
  "auxiliar_enfermagem",
  "instrumentador",
  "perfusionista",
  "fisioterapeuta",
  "farmaceutico",
  "biomedico",
  "tecnico_radiologia",
  "academico",
  "especialista_opme",
  "apoio",
  "outro"
] as const;
export type ProfessionalCategory = (typeof professionalCategories)[number];

export type ProfessionalCategoryInfo = {
  label: string;
  /** Conselho padrão pré-preenchido no formulário; `null` quando não há conselho típico. */
  defaultCouncil: CouncilType | null;
  /** Conselho e nº de registro obrigatórios para esta categoria. */
  councilRequired: boolean;
  /** Categoria médica: exibe especialidade e RQE. */
  medical: boolean;
  /** Validade de acesso obrigatória (acadêmicos e OPME). */
  accessWindowRequired: boolean;
};

export const professionalCategoryInfo: Readonly<Record<ProfessionalCategory, ProfessionalCategoryInfo>> = {
  medico_cirurgiao: { label: "Médico(a) cirurgião(ã)", defaultCouncil: "CRM", councilRequired: true, medical: true, accessWindowRequired: false },
  medico_anestesiologista: { label: "Médico(a) anestesiologista", defaultCouncil: "CRM", councilRequired: true, medical: true, accessWindowRequired: false },
  medico_auxiliar: { label: "Médico(a) auxiliar / assistente", defaultCouncil: "CRM", councilRequired: true, medical: true, accessWindowRequired: false },
  medico_residente: { label: "Médico(a) residente", defaultCouncil: "CRM", councilRequired: true, medical: true, accessWindowRequired: false },
  cirurgiao_dentista: { label: "Cirurgião(ã)-dentista (bucomaxilofacial)", defaultCouncil: "CRO", councilRequired: true, medical: false, accessWindowRequired: false },
  enfermeiro: { label: "Enfermeiro(a)", defaultCouncil: "COREN", councilRequired: true, medical: false, accessWindowRequired: false },
  tecnico_enfermagem: { label: "Técnico(a) de enfermagem", defaultCouncil: "COREN", councilRequired: true, medical: false, accessWindowRequired: false },
  auxiliar_enfermagem: { label: "Auxiliar de enfermagem", defaultCouncil: "COREN", councilRequired: true, medical: false, accessWindowRequired: false },
  instrumentador: { label: "Instrumentador(a) cirúrgico(a)", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: false },
  perfusionista: { label: "Perfusionista", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: false },
  fisioterapeuta: { label: "Fisioterapeuta", defaultCouncil: "CREFITO", councilRequired: true, medical: false, accessWindowRequired: false },
  farmaceutico: { label: "Farmacêutico(a)", defaultCouncil: "CRF", councilRequired: true, medical: false, accessWindowRequired: false },
  biomedico: { label: "Biomédico(a)", defaultCouncil: "CRBM", councilRequired: true, medical: false, accessWindowRequired: false },
  tecnico_radiologia: { label: "Técnico(a) em radiologia", defaultCouncil: "CRTR", councilRequired: true, medical: false, accessWindowRequired: false },
  academico: { label: "Acadêmico(a) / estudante", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: true },
  especialista_opme: { label: "Especialista de materiais / OPME", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: true },
  apoio: { label: "Apoio (higienização, maqueiro etc.)", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: false },
  outro: { label: "Outro", defaultCouncil: null, councilRequired: false, medical: false, accessWindowRequired: false }
};

export function professionalCategoryLabel(category: ProfessionalCategory | null | undefined): string {
  return category ? professionalCategoryInfo[category].label : "Cadastro incompleto";
}

export const employmentBonds = ["corpo_clinico", "clt", "cooperado", "terceirizado", "residente", "visitante"] as const;
export type EmploymentBond = (typeof employmentBonds)[number];

export const employmentBondLabels: Readonly<Record<EmploymentBond, string>> = {
  corpo_clinico: "Corpo clínico",
  clt: "CLT",
  cooperado: "Cooperado(a)",
  terceirizado: "Terceirizado(a)",
  residente: "Residente",
  visitante: "Visitante"
};

/** Vínculos que exigem validade de acesso (4.3). */
export const bondsRequiringAccessWindow: readonly EmploymentBond[] = ["residente", "visitante"];

export const brazilianStates = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR",
  "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
] as const;
export type BrazilianState = (typeof brazilianStates)[number];

export const specialtySuggestions = [
  "Anestesiologia",
  "Cirurgia Geral",
  "Ortopedia e Traumatologia",
  "Cirurgia Cardiovascular",
  "Neurocirurgia",
  "Urologia",
  "Ginecologia e Obstetrícia",
  "Cirurgia Plástica",
  "Otorrinolaringologia",
  "Oftalmologia",
  "Cirurgia Vascular",
  "Cirurgia Pediátrica",
  "Cirurgia Torácica",
  "Cirurgia do Aparelho Digestivo",
  "Coloproctologia",
  "Cirurgia de Cabeça e Pescoço",
  "Mastologia",
  "Cirurgia Oncológica"
] as const;

// ---------------------------------------------------------------------------
// Fluxo do paciente pela pulseira + mensageria (item 5)
// ---------------------------------------------------------------------------

export const registrationLanguages = ["pt-BR", "en"] as const;
export type RegistrationLanguage = (typeof registrationLanguages)[number];

export const registrationStatuses = ["active", "closed"] as const;
export type RegistrationStatus = (typeof registrationStatuses)[number];

export const stayEntrySources = ["scan", "quick_name"] as const;
export type StayEntrySource = (typeof stayEntrySources)[number];

export const stayExitSources = ["scan", "forced", "death"] as const;
export type StayExitSource = (typeof stayExitSources)[number];

export const notificationEventTypes = [
  "patient_entered_room",
  "patient_exited_room",
  "urgency_call",
  "post_discharge_48h",
  "phone_test"
] as const;
export type NotificationEventType = (typeof notificationEventTypes)[number];

export const notificationEventLabels: Readonly<Record<NotificationEventType, string>> = {
  patient_entered_room: "Entrada na sala",
  patient_exited_room: "Saída da sala",
  urgency_call: "Chamada de urgência",
  post_discharge_48h: "Pós-alta (48 h)",
  phone_test: "Mensagem de teste"
};

export const deliveryStatuses = ["pending", "processing", "sent", "failed", "cancelled"] as const;
export type DeliveryStatus = (typeof deliveryStatuses)[number];

export const deliveryStatusLabels: Readonly<Record<DeliveryStatus, string>> = {
  pending: "Pendente",
  processing: "Enviando",
  sent: "Enviada",
  failed: "Falhou",
  cancelled: "Cancelada"
};

export const accessLinkModes = ["reception", "room"] as const;
export type AccessLinkMode = (typeof accessLinkModes)[number];

export const accessLinkModeLabels: Readonly<Record<AccessLinkMode, string>> = {
  reception: "Recepção",
  room: "Sala"
};

export const registrationAlertReasons = ["quick_entry", "missing_companion_phone"] as const;
export type RegistrationAlertReason = (typeof registrationAlertReasons)[number];

export const registrationAlertResolutions = ["completed", "no_companion"] as const;
export type RegistrationAlertResolution = (typeof registrationAlertResolutions)[number];

/** Resultado de uma leitura de pulseira na sala (5.8). */
export const scanActions = ["entry", "exit_confirmation_required", "exit", "duplicate", "name_required"] as const;
export type ScanAction = (typeof scanActions)[number];
